// Admin Store for Public Schema Management
import { defineStore } from 'pinia'
import { adminApi } from '../services/adminApi.js'

export const useAdminStore = defineStore('admin', {
  state: () => ({
    // Authentication (JWT-based)
    isAuthenticated: false,
    
    // Dashboard Data
    dashboard: {
      total_companies: 0,
      active_companies: 0,
      total_users: 0,
      recent_invoices: []
    },
    
    // Companies
    companies: [],
    selectedCompany: null,
    
    // Invoices
    invoices: [],
    
    // UI State
    loading: false,
    error: null
  }),

  getters: {
    // Dashboard metrics
    companyGrowthRate: (state) => {
      return state.dashboard.active_companies > 0 
        ? ((state.dashboard.total_companies / state.dashboard.active_companies) * 100).toFixed(1)
        : 0
    },
    
    averageUsersPerCompany: (state) => {
      return state.dashboard.active_companies > 0 
        ? (state.dashboard.total_users / state.dashboard.active_companies).toFixed(1)
        : 0
    },

    // Company metrics
    activeCompanies: (state) => state.companies.filter(c => c.is_active),
    inactiveCompanies: (state) => state.companies.filter(c => !c.is_active),
    
    // Recent activity
    recentCompanies: (state) => {
      return state.companies
        .sort((a, b) => new Date(b.created_on) - new Date(a.created_on))
        .slice(0, 5)
    }
  },

  actions: {
    // ========== AUTHENTICATION ==========
    
    async login(credentials) {
      this.loading = true
      this.error = null
      
      try {
        const response = await adminApi.login(credentials)
        this.isAuthenticated = true
        // Load initial dashboard data now that JWT APIs are working!
        await this.loadDashboardData()
        
        return response
      } catch (error) {
        this.error = error.message
        throw error
      } finally {
        this.loading = false
      }
    },

    async logout() {
      try {
        await adminApi.logout()
      } catch (error) {
        console.warn('Logout API call failed:', error)
      } finally {
        this.clearSession()
      }
    },

    clearSession() {
      this.isAuthenticated = false
      // Remove sessionExpiry since JWT handles expiration internally
      this.dashboard = { total_companies: 0, active_companies: 0, total_users: 0, recent_invoices: [] }
      this.companies = []
      this.selectedCompany = null
      this.invoices = []
      adminApi.clearSession() // This clears the JWT token
    },

    async checkSession() {
      // For JWT, we don't need to check session expiry - the token handles that
      try {
        const isValid = await adminApi.checkSession()
        
        if (!isValid) {
          this.clearSession()
        } else {
          this.isAuthenticated = true
        }
        return isValid
      } catch (error) {
        this.clearSession()
        return false
      }
    },

    // ========== DASHBOARD ==========
    
    async loadDashboardData() {
      this.loading = true
      
      try {
        const data = await adminApi.getDashboardData()
        this.dashboard = data
      } catch (error) {
        this.error = error.message
        throw error
      } finally {
        this.loading = false
      }
    },

    // ========== COMPANIES MANAGEMENT ==========
    
    async loadCompanies() {
      this.loading = true
      
      try {
        const response = await adminApi.getCompanies()
        this.companies = response.companies || []
      } catch (error) {
        this.error = error.message
        throw error
      } finally {
        this.loading = false
      }
    },

    async loadCompanyDetails(id) {
      this.loading = true
      
      try {
        const company = await adminApi.getCompany(id)
        this.selectedCompany = company
        return company
      } catch (error) {
        this.error = error.message
        throw error
      } finally {
        this.loading = false
      }
    },

    async createCompany(companyData) {
      this.loading = true
      
      try {
        const newCompany = await adminApi.createCompany(companyData)
        this.companies.push(newCompany)
        
        // Refresh dashboard data
        await this.loadDashboardData()
        
        return newCompany
      } catch (error) {
        this.error = error.message
        throw error
      } finally {
        this.loading = false
      }
    },

    async updateCompany(id, companyData) {
      this.loading = true
      
      try {
        const updatedCompany = await adminApi.updateCompany(id, companyData)
        
        // Update in local state
        const index = this.companies.findIndex(c => c.id === id)
        if (index !== -1) {
          this.companies[index] = updatedCompany
        }
        
        if (this.selectedCompany && this.selectedCompany.id === id) {
          this.selectedCompany = updatedCompany
        }
        
        return updatedCompany
      } catch (error) {
        this.error = error.message
        throw error
      } finally {
        this.loading = false
      }
    },

    async deleteCompany(id) {
      this.loading = true
      
      try {
        await adminApi.deleteCompany(id)
        
        // Remove from local state
        this.companies = this.companies.filter(c => c.id !== id)
        
        if (this.selectedCompany && this.selectedCompany.id === id) {
          this.selectedCompany = null
        }
        
        // Refresh dashboard data
        await this.loadDashboardData()
        
      } catch (error) {
        this.error = error.message
        throw error
      } finally {
        this.loading = false
      }
    },

    // ========== INVOICES MANAGEMENT ==========
    
    async loadInvoices() {
      this.loading = true
      
      try {
        const invoices = await adminApi.getInvoices()
        this.invoices = invoices
      } catch (error) {
        this.error = error.message
        throw error
      } finally {
        this.loading = false
      }
    },

    async createInvoice(invoiceData) {
      this.loading = true
      
      try {
        const newInvoice = await adminApi.createInvoice(invoiceData)
        this.invoices.push(newInvoice)
        
        // Refresh dashboard data to update recent invoices
        await this.loadDashboardData()
        
        return newInvoice
      } catch (error) {
        this.error = error.message
        throw error
      } finally {
        this.loading = false
      }
    },

    // ========== UTILITY ==========
    
    clearError() {
      this.error = null
    },

    showMessage(message, type = 'success') {
      // This can be enhanced with a notification system
    }
  }
})